import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import UserAvatar from '../components/UserAvatar';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ColumnCustomizer from '../components/ColumnCustomizer';
import TaskBillingStatusBadges from '../components/TaskBillingStatusBadges';
import { getStatusBadgeClass } from '../utils/taskStatus';
import { formatTaskStatus, formatTaskPriority } from '../utils/taskLabels';
import { formatDate as formatLocaleDate, formatDateTime } from '../utils/locale';
import { formatMinutesAsHours } from '../utils/classifiedHoursDisplay';
import { completedTimeTotalsByTaskId } from '../utils/taskCheckInTime';
import { formatError } from '../utils/errorHandler';
import { canAccessFeature, isCompanyAdmin } from '../utils/planAccess';
import { BILLING_FILTERS, matchesBillingFilter, mergeTaskBilling } from '../utils/taskBilling';
import {
  downloadTasksCsv,
  downloadTasksJson,
  downloadFortnoxSelectionJson
} from '../utils/taskDataExport';
import './Tasks.css';
import './TaskLog.css';

const toDateInput = (date) => date.toISOString().slice(0, 10);

const defaultCompletedRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { startDate: toDateInput(start), endDate: toDateInput(end) };
};

const DEFAULT_VISIBLE_COLUMNS = {
  location: true,
  status: true,
  billing: true,
  title: true,
  priority: true,
  checklist: true,
  assignedTo: true,
  created: true,
  completed: true,
  timeSpent: true,
  lunch: true
};

const TaskLog = () => {
  const { t, i18n } = useTranslation(['taskLog', 'common', 'tasks', 'export']);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const showBillingControls = isCompanyAdmin(user);
  const canExport = canAccessFeature(user, 'exportData');
  const [tasks, setTasks] = useState([]);
  const [timeByTaskId, setTimeByTaskId] = useState(() => new Map());
  const [lunchByTaskId, setLunchByTaskId] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [timeWarning, setTimeWarning] = useState('');
  const [groupedTasks, setGroupedTasks] = useState({});
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [sorting, setSorting] = useState({});
  const [billingFilter, setBillingFilter] = useState('all');
  const [selectedTasks, setSelectedTasks] = useState(() => new Set());
  const [startDate, setStartDate] = useState(() => defaultCompletedRange().startDate);
  const [endDate, setEndDate] = useState(() => defaultCompletedRange().endDate);

  const columnLabels = useMemo(() => {
    const labels = {
      location: t('columnLocation'),
      status: t('columnStatus'),
      title: t('columnTitle'),
      priority: t('columnPriority'),
      checklist: t('columnChecklist'),
      assignedTo: t('columnAssignedTo'),
      created: t('columnCreated'),
      completed: t('columnCompleted'),
      timeSpent: t('columnTimeSpent'),
      lunch: t('columnLunch')
    };
    if (showBillingControls) {
      labels.billing = t('columnBilling');
    }
    return labels;
  }, [t, showBillingControls]);

  const filteredTasks = useMemo(() => {
    if (!showBillingControls || billingFilter === 'all') {
      return tasks;
    }
    return tasks.filter((task) => matchesBillingFilter(task, billingFilter));
  }, [tasks, showBillingControls, billingFilter]);

  const handleRowClick = (taskId) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleSort = (dateKey, column) => {
    setSorting(prev => {
      const current = prev[dateKey] || { column: null, direction: null };
      if (current.column === column) {
        if (current.direction === 'asc') {
          return { ...prev, [dateKey]: { column, direction: 'desc' } };
        } else if (current.direction === 'desc') {
          return { ...prev, [dateKey]: { column: null, direction: null } };
        }
      }
      return { ...prev, [dateKey]: { column, direction: 'asc' } };
    });
  };

  const renderSortableHeader = (dateKey, column, label) => {
    const sortConfig = sorting[dateKey] || { column: null, direction: null };
    const isSorted = sortConfig.column === column;
    const sortDirection = isSorted ? sortConfig.direction : null;

    return (
      <th
        className="task-sortable-header"
        onClick={() => handleSort(dateKey, column)}
      >
        <div className="task-sortable-header-inner">
          <span>{label}</span>
          {sortDirection === 'asc' && (
            <span className="material-symbols-outlined task-sort-icon">arrow_upward</span>
          )}
          {sortDirection === 'desc' && (
            <span className="material-symbols-outlined task-sort-icon">arrow_downward</span>
          )}
        </div>
      </th>
    );
  };

  const sortTasks = (taskList, dateKey) => {
    const sortConfig = sorting[dateKey] || { column: null, direction: null };
    if (!sortConfig.column || !sortConfig.direction) {
      return taskList;
    }

    const sorted = [...taskList].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.column) {
        case 'location':
          aValue = (a.location_name || a.location || '').toLowerCase();
          bValue = (b.location_name || b.location || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'priority': {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        }
        case 'checklist': {
          let aChecklist = [];
          if (a.checklist) {
            if (Array.isArray(a.checklist)) {
              aChecklist = a.checklist;
            } else if (typeof a.checklist === 'string') {
              try {
                aChecklist = JSON.parse(a.checklist);
              } catch (e) {
                aChecklist = [];
              }
            }
          }
          let bChecklist = [];
          if (b.checklist) {
            if (Array.isArray(b.checklist)) {
              bChecklist = b.checklist;
            } else if (typeof b.checklist === 'string') {
              try {
                bChecklist = JSON.parse(b.checklist);
              } catch (e) {
                bChecklist = [];
              }
            }
          }
          aValue = aChecklist.length;
          bValue = bChecklist.length;
          break;
        }
        case 'assignedTo':
          aValue = (a.assigned_user_names || '').toLowerCase();
          bValue = (b.assigned_user_names || '').toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'completed':
          aValue = new Date(a.updated_at || a.created_at || 0).getTime();
          bValue = new Date(b.updated_at || b.created_at || 0).getTime();
          break;
        case 'timeSpent':
          aValue = timeByTaskId.has(Number(a.id)) ? timeByTaskId.get(Number(a.id)) : -1;
          bValue = timeByTaskId.has(Number(b.id)) ? timeByTaskId.get(Number(b.id)) : -1;
          break;
        case 'lunch':
          aValue = lunchByTaskId.has(Number(a.id)) ? lunchByTaskId.get(Number(a.id)) : -1;
          bValue = lunchByTaskId.has(Number(b.id)) ? lunchByTaskId.get(Number(b.id)) : -1;
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    });

    return sorted;
  };

  const groupTasksByDate = useCallback(() => {
    const grouped = {};
    filteredTasks.forEach((task) => {
      const completedDate = new Date(task.updated_at || task.created_at);
      const dateKey = completedDate.toISOString().split('T')[0];

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(task);
    });

    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
    const sortedGrouped = {};
    sortedDates.forEach((date) => {
      sortedGrouped[date] = grouped[date].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at);
        const dateB = new Date(b.updated_at || b.created_at);
        return dateB - dateA;
      });
    });

    setGroupedTasks(sortedGrouped);
  }, [filteredTasks]);

  const fetchCompletedTasks = useCallback(async () => {
    if (startDate && endDate && startDate > endDate) {
      setError(t('invalidDateRange'));
      setTasks([]);
      setSelectedTasks(new Set());
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setTimeWarning('');
      setError('');
      const params = {
        status: 'completed',
        limit: 500
      };
      if (startDate) {
        params.completedFrom = startDate;
      }
      if (endDate) {
        params.completedTo = endDate;
      }
      const response = await axios.get('/api/tasks', { params });
      const completed = response.data.data || [];
      setTasks(completed);
      setSelectedTasks(new Set());

      let nextTimeByTask = new Map();
      let nextLunchByTask = new Map();
      if (completed.length > 0) {
        const taskIdList = completed.map((task) => Number(task.id)).filter(Number.isFinite);
        try {
          const checkInsResponse = await axios.get('/api/check-ins', {
            params: { taskIds: taskIdList.join(',') }
          });
          const totals = completedTimeTotalsByTaskId(checkInsResponse.data?.data || []);
          nextTimeByTask = totals.net;
          nextLunchByTask = totals.lunch;
        } catch (checkInError) {
          console.error('Error fetching check-ins for task log:', checkInError);
          setTimeWarning(t('timeFetchWarning'));
        }
      }
      setTimeByTaskId(nextTimeByTask);
      setLunchByTaskId(nextLunchByTask);
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
      setError(t('failedFetch'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, t]);

  useEffect(() => {
    fetchCompletedTasks();
  }, [fetchCompletedTasks]);

  useEffect(() => {
    groupTasksByDate();
  }, [groupTasksByDate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDate = new Date(date);
    taskDate.setHours(0, 0, 0, 0);

    const fullDate = formatLocaleDate(date, i18n.language, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    if (taskDate.getTime() === today.getTime()) {
      return `${t('common:today')}, ${fullDate}`;
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (taskDate.getTime() === yesterday.getTime()) {
      return `${t('common:yesterday')}, ${fullDate}`;
    }
    return fullDate;
  };

  const getDateKeys = () => {
    return Object.keys(groupedTasks).sort((a, b) => new Date(b) - new Date(a));
  };

  const getAssigneeLabel = (task) => {
    if (task.assigned_users && task.assigned_users.length > 0) {
      return task.assigned_users
        .map((u) => `${u.firstName || ''} ${u.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ');
    }
    if (task.assigned_user_names && task.assigned_user_names.trim()) {
      return task.assigned_user_names;
    }
    return t('common:unassigned');
  };

  const handleTaskSelect = (taskId, checked, event) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(taskId);
      } else {
        next.delete(taskId);
      }
      return next;
    });
  };

  const handleSelectGroup = (taskList, checked) => {
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      taskList.forEach((task) => {
        if (checked) {
          next.add(task.id);
        } else {
          next.delete(task.id);
        }
      });
      return next;
    });
  };

  const selectedTasksList = useMemo(
    () => tasks.filter((task) => selectedTasks.has(task.id)),
    [tasks, selectedTasks]
  );

  const requireSelection = () => {
    if (selectedTasks.size === 0) {
      setError(t('export:selectAtLeastOne'));
      return false;
    }
    return true;
  };

  const bulkMarkBilling = async (payload) => {
    if (!showBillingControls || selectedTasks.size === 0) {
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await axios.patch('/api/tasks/billing-status/bulk', {
        taskIds: [...selectedTasks],
        ...payload
      });
      const updatedRows = response.data?.data?.updated || [];
      const byId = new Map(updatedRows.map((row) => [row.id, row]));
      setTasks((prev) => prev.map((task) => mergeTaskBilling(task, byId.get(task.id)) || task));
    } catch (err) {
      setError(formatError(err) || t('tasks:billingStatusUpdateFailed'));
    } finally {
      setBusy(false);
    }
  };

  const exportToCSV = () => {
    if (!canExport || !requireSelection()) return;
    downloadTasksCsv(selectedTasksList, t);
    setError('');
  };

  const exportToJSON = () => {
    if (!canExport || !requireSelection()) return;
    downloadTasksJson(selectedTasksList);
    setError('');
  };

  const exportFortnoxJSON = () => {
    if (!canExport || !requireSelection()) return;
    downloadFortnoxSelectionJson(selectedTasksList);
    setError('');
  };

  const renderTaskLogMobileCard = (task) => {
    const taskId = Number(task.id);
    const hasTime = timeByTaskId.has(taskId);
    const hasLunch = lunchByTaskId.has(taskId);
    const timeLabel = hasTime ? formatMinutesAsHours(timeByTaskId.get(taskId)) : null;
    const lunchLabel = hasLunch ? formatMinutesAsHours(lunchByTaskId.get(taskId)) : null;
    return (
      <article
        key={task.id}
        className="tasks-redesign-card"
        onClick={() => handleRowClick(task.id)}
      >
        <div className="tasks-redesign-card-body">
          <div className="tasks-redesign-card-top">
            {showBillingControls && (
              <label
                className="task-log-select"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedTasks.has(task.id)}
                  onChange={(e) => handleTaskSelect(task.id, e.target.checked)}
                />
              </label>
            )}
            <h3 className="tasks-redesign-card-title">{task.title}</h3>
            <span className={getStatusBadgeClass(task.status)}>
              {formatTaskStatus(task.status, t)}
            </span>
          </div>
          {showBillingControls && (
            <div className="tasks-redesign-card-meta">
              <TaskBillingStatusBadges task={task} iconsOnly />
            </div>
          )}
          <div className="tasks-redesign-card-meta">
            <span className="tasks-redesign-meta-item">
              <span className="material-symbols-outlined" aria-hidden="true">person</span>
              {getAssigneeLabel(task)}
            </span>
            <span className="tasks-redesign-meta-item">
              <span className="material-symbols-outlined" aria-hidden="true">schedule</span>
              {t('completedAt', {
                date: formatDateTime(task.updated_at || task.created_at)
              })}
            </span>
            {timeLabel && (
              <span className="tasks-redesign-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">timer</span>
                {t('timeSpentMeta', { time: timeLabel })}
              </span>
            )}
            {lunchLabel && (
              <span className="tasks-redesign-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">restaurant</span>
                {t('lunchMeta', { time: lunchLabel })}
              </span>
            )}
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const dateKeys = getDateKeys();
  const cols = visibleColumns;
  const customizerLabels = columnLabels;

  return (
    <div className="task-log">
      <div className="container container--wide main-content">
        <div className="page-header">
          <h1>{t('pageTitle')}</h1>
          <div className="page-header-actions">
            {dateKeys.length > 0 && (
              <ColumnCustomizer
                columnLabels={customizerLabels}
                visibleColumns={visibleColumns}
                onChange={(key, checked) => setVisibleColumns((prev) => ({ ...prev, [key]: checked }))}
                label={t('tasks:customizeColumns')}
                ariaLabel={t('common:columnVisibility')}
              />
            )}
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/tasks')}>
              {t('backToTasks')}
            </button>
          </div>
        </div>

        {error && <div className="error">{error}</div>}
        {timeWarning && <div className="error">{timeWarning}</div>}

        <div className="card task-log-date-filters">
          <div className="task-log-date-filter-row" role="group" aria-label={t('dateRangeAria')}>
            <div>
              <label htmlFor="task-export-start">{t('export:startDateLabel')}</label>
              <input
                id="task-export-start"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="task-export-end">{t('export:endDateLabel')}</label>
              <input
                id="task-export-end"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
          </div>
        </div>

        {showBillingControls && tasks.length > 0 && (
          <div className="task-log-billing-toolbar">
            <div
              className="tasks-view-toggle tasks-billing-filter"
              role="group"
              aria-label={t('tasks:billingFilterAria')}
            >
              {BILLING_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  className={`tasks-view-toggle-btn${billingFilter === filter.id ? ' is-active' : ''}`}
                  onClick={() => setBillingFilter(filter.id)}
                >
                  {t(`tasks:${filter.labelKey}`)}
                </button>
              ))}
            </div>
            <div className="task-log-billing-actions">
              <span className="task-log-selection-count">
                {t('tasksSelected', { count: selectedTasks.size })}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy || selectedTasks.size === 0}
                onClick={() => bulkMarkBilling({ attested: true })}
              >
                {t('bulkAttest')}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={busy || selectedTasks.size === 0}
                onClick={() => bulkMarkBilling({ invoiced: true })}
              >
                {t('bulkInvoice')}
              </button>
              {canExport && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={selectedTasks.size === 0}
                    onClick={exportToJSON}
                  >
                    {t('export:exportJson')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={selectedTasks.size === 0}
                    onClick={exportFortnoxJSON}
                  >
                    {t('export:exportFortnoxJson')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    disabled={selectedTasks.size === 0}
                    onClick={exportToCSV}
                  >
                    {t('export:exportSelected', { count: selectedTasks.size })}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {dateKeys.length === 0 ? (
          <EmptyState
            icon="history"
            title={
              tasks.length > 0
                ? t('tasks:billingFilterEmptyTitle')
                : (startDate || endDate ? t('noCompletedInRangeTitle') : t('noCompletedTitle'))
            }
            description={
              tasks.length > 0
                ? t('tasks:billingFilterEmptyDescription')
                : (startDate || endDate ? t('noCompletedInRangeDescription') : t('noCompletedDescription'))
            }
          />
        ) : (
          dateKeys.map((dateKey, index) => {
            const sortedTasks = sortTasks(groupedTasks[dateKey] || [], dateKey);
            const allGroupSelected = sortedTasks.length > 0
              && sortedTasks.every((task) => selectedTasks.has(task.id));

            return (
              <React.Fragment key={dateKey}>
                {index > 0 && <hr className="tasks-section-divider" aria-hidden="true" />}
                <section className="tasks-section">
                  <div className="tasks-section-header">
                    <h2 className="tasks-section-heading">{formatDate(dateKey)}</h2>
                  </div>
                  {sortedTasks.length > 0 ? (
                    <>
                      <div className="tasks-list-table-wrap tasks-list-table-desktop">
                        <table className="table tasks-list-table">
                          <thead>
                            <tr>
                              {showBillingControls && (
                                <th style={{ width: '40px' }}>
                                  <input
                                    type="checkbox"
                                    checked={allGroupSelected}
                                    onChange={(e) => handleSelectGroup(sortedTasks, e.target.checked)}
                                    aria-label={t('selectAll')}
                                  />
                                </th>
                              )}
                              {cols.location && renderSortableHeader(dateKey, 'location', columnLabels.location)}
                              {cols.status && renderSortableHeader(dateKey, 'status', columnLabels.status)}
                              {cols.billing && showBillingControls && (
                                <th>{columnLabels.billing}</th>
                              )}
                              {cols.title && renderSortableHeader(dateKey, 'title', columnLabels.title)}
                              {cols.priority && renderSortableHeader(dateKey, 'priority', columnLabels.priority)}
                              {cols.checklist && renderSortableHeader(dateKey, 'checklist', columnLabels.checklist)}
                              {cols.assignedTo && renderSortableHeader(dateKey, 'assignedTo', columnLabels.assignedTo)}
                              {cols.created && renderSortableHeader(dateKey, 'created', columnLabels.created)}
                              {cols.completed && renderSortableHeader(dateKey, 'completed', columnLabels.completed)}
                              {cols.timeSpent && renderSortableHeader(dateKey, 'timeSpent', columnLabels.timeSpent)}
                              {cols.lunch && renderSortableHeader(dateKey, 'lunch', columnLabels.lunch)}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedTasks.map((task) => {
                              const hasAssignedUsers = task.assigned_users && task.assigned_users.length > 0;
                              const hasAssignedNames = task.assigned_user_names && task.assigned_user_names.trim() !== '';

                              let checklist = [];
                              if (task.checklist) {
                                if (Array.isArray(task.checklist)) {
                                  checklist = task.checklist;
                                } else if (typeof task.checklist === 'string') {
                                  try {
                                    checklist = JSON.parse(task.checklist);
                                  } catch (e) {
                                    checklist = [];
                                  }
                                }
                              }
                              const normalizedChecklist = checklist.map(item => {
                                if (typeof item === 'string') {
                                  return { text: item, checked: false };
                                }
                                return { text: item.text || item, checked: item.checked || false };
                              });
                              const checkedCount = normalizedChecklist.filter(item => item.checked).length;
                              const totalCount = normalizedChecklist.length;

                              return (
                                <tr
                                  key={task.id}
                                  className="tasks-list-table-row"
                                  onClick={() => handleRowClick(task.id)}
                                >
                                  {showBillingControls && (
                                    <td onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={selectedTasks.has(task.id)}
                                        onChange={(e) => handleTaskSelect(task.id, e.target.checked)}
                                        aria-label={t('selectTask', { title: task.title })}
                                      />
                                    </td>
                                  )}
                                  {cols.location && <td>{task.location_name || task.location || t('common:emptyCell')}</td>}
                                  {cols.status && (
                                    <td>
                                      <span className={getStatusBadgeClass(task.status)}>
                                        {formatTaskStatus(task.status, t)}
                                      </span>
                                    </td>
                                  )}
                                  {cols.billing && showBillingControls && (
                                    <td>
                                      <TaskBillingStatusBadges task={task} iconsOnly />
                                      {!task.attested && !task.invoiced ? t('common:emptyCell') : null}
                                    </td>
                                  )}
                                  {cols.title && <td className="tasks-list-table-title">{task.title}</td>}
                                  {cols.priority && <td>{formatTaskPriority(task.priority, t)}</td>}
                                  {cols.checklist && (
                                    <td>{totalCount > 0 ? `${checkedCount}/${totalCount}` : t('common:emptyCell')}</td>
                                  )}
                                  {cols.assignedTo && (
                                    <td>
                                      {hasAssignedUsers ? (
                                        <div className="task-assignee-list">
                                          {task.assigned_users.map((assignedUser, idx) => (
                                            <div key={idx} className="task-assignee-chip">
                                              <UserAvatar
                                                user={{
                                                  first_name: assignedUser.firstName,
                                                  last_name: assignedUser.lastName,
                                                  profile_image_url: assignedUser.profile_image_url
                                                }}
                                                size={28}
                                              />
                                              <span className="task-assignee-name">
                                                {assignedUser.firstName} {assignedUser.lastName}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      ) : hasAssignedNames ? (
                                        <span>{task.assigned_user_names}</span>
                                      ) : (
                                        t('common:emptyCell')
                                      )}
                                    </td>
                                  )}
                                  {cols.created && (
                                    <td>
                                      {formatLocaleDate(task.created_at)}
                                    </td>
                                  )}
                                  {cols.completed && (
                                    <td>
                                      {formatDateTime(task.updated_at || task.created_at)}
                                    </td>
                                  )}
                                  {cols.timeSpent && (
                                    <td>
                                      {timeByTaskId.has(Number(task.id))
                                        ? formatMinutesAsHours(timeByTaskId.get(Number(task.id)))
                                        : t('common:emptyCell')}
                                    </td>
                                  )}
                                  {cols.lunch && (
                                    <td>
                                      {lunchByTaskId.has(Number(task.id))
                                        ? formatMinutesAsHours(lunchByTaskId.get(Number(task.id)))
                                        : t('common:emptyCell')}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="tasks-card-grid tasks-list-cards-mobile">
                        {sortedTasks.map((task) => renderTaskLogMobileCard(task))}
                      </div>
                    </>
                  ) : (
                    <EmptyState
                      icon="history"
                      title={t('noTasksOnDateTitle')}
                      description={t('noTasksOnDateDescription')}
                    />
                  )}
                </section>
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TaskLog;
